import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Plus, Search } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const REVIEW = ["DRAFT", "STRUCTURING", "STORYBOARD_READY", "AWAITING_APPROVAL", "CHANGES_REQUESTED"];
const bucket = (m) => {
    if (m.render_status === "FAILED") return "failed";
    if (m.status === "PUBLISHED") return "done";
    if (m.render_status === "READY") return "ready";
    if (REVIEW.includes(m.status)) return "review";
    return "other";
};
const LABEL = { review: "Needs review", ready: "Ready", done: "Done", failed: "Failed", other: "In progress" };
const PILL = {
    review: "bg-amber-50 text-amber-800", ready: "bg-blue-50 text-blue-800", done: "bg-emerald-50 text-emerald-800",
    failed: "bg-red-50 text-red-700", other: "bg-neutral-100 text-neutral-700",
};
const FILTERS = [["all", "All"], ["review", "Needs review"], ["ready", "Ready"], ["done", "Done"], ["failed", "Failed"]];

function Row({ m, extra, open, onToggle }) {
    const b = bucket(m);
    return (
        <div className="flex items-center gap-3 px-4 py-3">
            {extra ? (
                <button type="button" onClick={onToggle} data-testid={`videos-group-toggle-${m.id}`} className="text-neutral-500 hover:text-neutral-900">
                    {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
            ) : <span className="w-[14px]" />}
            <Link to={`/videos/${m.id}`} data-testid={`videos-row-${m.id}`} className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-neutral-900">{m.name}</div>
                <div className="truncate text-xs text-neutral-500">{m.document_title || "—"} · {m.scene_count ?? 0} scenes</div>
            </Link>
            {extra > 0 && <span className="text-xs text-neutral-500">+{extra} other version{extra === 1 ? "" : "s"}</span>}
            <span data-testid={`videos-status-${m.id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${PILL[b]}`}>{LABEL[b]}</span>
        </div>
    );
}

export default function VideosList() {
    const nav = useNavigate();
    const [mods, setMods] = useState([]);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [open, setOpen] = useState({});
    useEffect(() => { api.get("/modules").then((r) => setMods(r.data)).catch((e) => setErr(formatApiError(e))); }, []);

    const groups = useMemo(() => {
        const by = new Map();
        mods.forEach((m) => {
            const k = `${m.document_id || ""}|${(m.name || "").trim().toLowerCase()}`;
            by.set(k, [...(by.get(k) || []), m]);
        });
        return [...by.values()].map((list) => {
            const sorted = [...list].sort((a, b) => (bucket(a) === "failed") - (bucket(b) === "failed")
                || (b.updated_at || "").localeCompare(a.updated_at || ""));
            return { main: sorted[0], rest: sorted.slice(1) };
        }).filter(({ main, rest }) => {
            const s = q.trim().toLowerCase();
            const hit = !s || [main, ...rest].some((m) => `${m.name} ${m.document_title || ""}`.toLowerCase().includes(s));
            const f = filter === "all" || [main, ...rest].some((m) => bucket(m) === filter);
            return hit && f;
        });
    }, [mods, q, filter]);

    return (
        <div data-testid="videos-page" className="space-y-6">
            <div className="flex items-center justify-between gap-4">
                <h1 className="text-3xl font-medium tracking-tight text-neutral-900">Videos</h1>
                <Button data-testid="videos-new-btn" onClick={() => nav("/onboard")} className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800">
                    <Plus size={14} className="mr-1.5" /> New training video
                </Button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-72">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
                    <Input data-testid="videos-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search videos" className="pl-8" />
                </div>
                {FILTERS.map(([k, l]) => (
                    <button key={k} type="button" data-testid={`videos-filter-${k}`} onClick={() => setFilter(k)}
                        className={`rounded-full border px-3 py-1 text-xs ${filter === k ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"}`}>
                        {l}
                    </button>
                ))}
            </div>
            {err && <div data-testid="videos-error" className="text-xs text-red-700">{err}</div>}
            <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white">
                {groups.map(({ main, rest }) => (
                    <div key={main.id}>
                        <Row m={main} extra={rest.length} open={!!open[main.id]} onToggle={() => setOpen((o) => ({ ...o, [main.id]: !o[main.id] }))} />
                        {open[main.id] && (
                            <div data-testid={`videos-group-${main.id}`} className="bg-neutral-50/60 pl-6">
                                {rest.map((m) => <Row key={m.id} m={m} />)}
                            </div>
                        )}
                    </div>
                ))}
                {!groups.length && <div data-testid="videos-empty" className="px-4 py-8 text-center text-sm text-neutral-500">No videos match.</div>}
            </div>
        </div>
    );
}
